import React, { useState, useEffect, useRef } from "react";
import { Box, useTheme } from "@mui/material";
import CodeMirror from "@uiw/react-codemirror";

export default function LuaCodeEditorInput({
  initialCode = `print("Hello, Lua!")`,
  onCodeChange,
  themeName: externalTheme,
  editable = true
}) {
  const [code, setCode] = useState(initialCode || "");
  const lastEmittedRef = useRef(initialCode || "");
  const muiTheme = useTheme();
  const isDarkMode = muiTheme.palette.mode === "dark";

  // Only update code when initialCode actually changes from outside (e.g., language switch)
  useEffect(() => {
    if (initialCode !== undefined && initialCode !== lastEmittedRef.current) {
      lastEmittedRef.current = initialCode;
      setCode(initialCode);
    }
  }, [initialCode]);

  const handleChange = (value) => {
    lastEmittedRef.current = value;
    setCode(value);
    onCodeChange?.(value);
  };

  const editorBg = isDarkMode ? "rgba(255,255,255,0.03)" : "#fafafa";

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        "& .cm-editor": {
          backgroundColor: editorBg,
          borderRadius: 0,
          fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace !important",
          fontSize: "0.95rem !important",
          height: "100%",
          minHeight: "100%",
          border: "none",
        },
        "& .cm-scroller": {
          height: "100%",
          minHeight: "100%",
          overflow: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: isDarkMode
            ? "rgba(255, 255, 255, 0.2) transparent"
            : "rgba(0, 0, 0, 0.2) transparent",
          "&::-webkit-scrollbar": { width: "6px", height: "6px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.35)" : "rgba(0, 0, 0, 0.35)",
          },
        },
        "& .cm-content": {
          minHeight: "100%",
          padding: "8px 0",
        },
        "& .cm-gutters": {
          minHeight: "100% !important",
          backgroundColor: isDarkMode ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.02)",
          color: isDarkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.35)",
          borderRight: `1px solid ${muiTheme.palette.divider}`,
        },
      }}
    >
      <CodeMirror
        value={code}
        height="100%"
        extensions={[]}
        theme={isDarkMode ? "dark" : "light"}
        onChange={handleChange}
        editable={editable}
        basicSetup={{
          lineNumbers: true,
          highlightActiveLine: editable,
          foldGutter: true,
          dropCursor: false,
          allowMultipleSelections: false,
          indentOnInput: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: true,
        }}
      />
    </Box>
  );
}

